import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TotalsProgressBar } from '../ActivityTotals/TotalsProgressBar/TotalsProgressBar';
import CompactPageOwnerAvatarStory from '../CompactPageOwnerStory/CompactPageOwnerAvatarStory';
import TruncatedPageOwnerTitle from '../PageOwnerTitle/PageTitle';
import styles from './InstagramShare.scss';
import { PAGES_UI_FEATURE_IMAGE } from '../../constants/imageTemplateStrings';
import { JustGivingLogo } from '../JustGivingLogo/JustGivingLogo';
import classNames from 'classnames';
import SVG from '@justgiving/svg';
import { alertIcon, successIcon } from '@justgiving/icons';

export enum IG_SHARE_TYPE {
  POST,
  STORY,
}

interface InstagramShareProps {
  heroImageUrl: string;
  altText: string;
  charityName: string;
  pageUrl: string;
  type: IG_SHARE_TYPE;
}

export const InstagramShare: React.FunctionComponent<InstagramShareProps> = ({
  heroImageUrl,
  altText,
  charityName,
  pageUrl,
  type,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const [copyState, setCopyState] = useState('');

  const postCopy = `${t('shareModal.instagram.raisingFor', {
    charityName: charityName,
  })} ${t('shareModal.instagram.supportMeAt', {
    pageUrl: pageUrl,
  })}`;

  const storyCopy = pageUrl;
  const isStory = type === IG_SHARE_TYPE.STORY;
  const isCopySuccess = copyState === 'success';
  const copyText = isStory ? storyCopy : postCopy;

  async function copy() {
    try {
      await navigator.clipboard.writeText(copyText);
      setCopyState('success');
    } catch {
      setCopyState('error');
    }
  }

  useEffect(() => {
    copy();
  }, [copyText]);

  return (
    <>
      <div className={isStory ? styles.storyContainer : ''}>
        <div
          className={classNames(
            styles.shareContainer,
            isStory
              ? styles.shareContainer__story
              : styles.shareContainer__post,
          )}
        >
          {isStory && (
            <div className={styles.logo}>
              <JustGivingLogo />
            </div>
          )}
          <img
            src={`${heroImageUrl}?template=${PAGES_UI_FEATURE_IMAGE}`}
            alt={altText}
            className={
              isStory
                ? styles.shareModalImage__story
                : styles.shareModalImage__post
            }
          />
          <div className={styles.shareTextAndTotals}>
            <TruncatedPageOwnerTitle truncateAt={isStory ? 75 : 60} />
            <CompactPageOwnerAvatarStory linkToFrp={true} />
            <TotalsProgressBar isInstagramShare={true} />
          </div>
        </div>
      </div>
      <div
        className={
          isStory ? styles.captionContainer__story : styles.captionContainer
        }
      >
        <>
          <p className={styles.captionContainerTitle}>
            {t('shareModal.instagram.shareLinkCtaPost')}
          </p>
          <div
            className={
              isStory
                ? styles.innerCaptionCopy__story
                : styles.innerCaptionCopy__post
            }
          >
            {isStory ? (
              <span>{pageUrl}</span>
            ) : (
              <>
                <p>
                  {t('shareModal.instagram.raisingFor', {
                    charityName: charityName,
                  })}
                </p>
                <p>
                  {t('shareModal.instagram.supportMeAt', {
                    pageUrl: pageUrl,
                  })}
                </p>
              </>
            )}
          </div>
          {}
          <div
            className={
              isCopySuccess ? styles.copyToastSuccess : styles.copyToastError
            }
          >
            <SVG
              markup={isCopySuccess ? successIcon : alertIcon}
              className={styles.icon}
            />
            <div className={styles.message}>
              {isCopySuccess ? (
                <>
                  {t('shareModal.instagram.copyLink')}{' '}
                  <button className={styles.urlCopy} onClick={copy}>
                    {t('shareModal.instagram.copyAgain')}
                  </button>
                </>
              ) : (
                <>
                  {t('shareModal.instagram.copyLinkFailed')}
                  <button className={styles.urlCopy} onClick={copy}>
                    {t('shareModal.instagram.copyManually')}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      </div>
    </>
  );
};
